import { Injectable } from '@angular/core';

/**
 * Service for generating device fingerprints
 * SECURITY FIX: VAPT Issue #3 - Iteration 2
 *
 * Generates a fingerprint based on browser characteristics to help detect
 * unauthorized access and token theft across different devices.
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceFingerprintService {
  private cachedFingerprint: string | null = null;

  constructor() {}

  /**
   * Generate a device fingerprint based on browser characteristics
   * This should match the backend DeviceFingerprintService logic
   */
  async generateFingerprint(): Promise<string> {
    // Return cached fingerprint if already generated
    if (this.cachedFingerprint) {
      return this.cachedFingerprint;
    }

    // Collect browser characteristics
    const components = [
      navigator.userAgent || '',
      navigator.language || '',
      this.getAcceptEncoding(),
      this.getAcceptHeader(),
      // Additional characteristics for more robust fingerprinting
      screen.colorDepth.toString(),
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset().toString(),
      this.hasSessionStorage().toString(),
      this.hasLocalStorage().toString(),
      this.hasIndexedDB().toString(),
    ];

    // Combine all components
    const combined = components.join('|');

    // Generate SHA-256 hash
    const fingerprint = await this.sha256(combined);

    // Cache the fingerprint
    this.cachedFingerprint = fingerprint;

    return fingerprint;
  }

  /**
   * Get the fingerprint synchronously if already generated
   * Returns null if not yet generated - use generateFingerprint() for first time
   */
  getFingerprint(): string | null {
    return this.cachedFingerprint;
  }

  /**
   * Clear cached fingerprint (useful for testing or when user changes device characteristics)
   */
  clearFingerprint(): void {
    this.cachedFingerprint = null;
  }

  /**
   * Get Accept-Encoding header value (approximation since we can't access actual headers)
   */
  private getAcceptEncoding(): string {
    // Modern browsers typically support these encodings
    return 'gzip, deflate, br';
  }

  /**
   * Get Accept header value (approximation)
   */
  private getAcceptHeader(): string {
    // Standard browser accept header
    return 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8';
  }

  /**
   * Check if sessionStorage is available
   */
  private hasSessionStorage(): boolean {
    try {
      return typeof sessionStorage !== 'undefined';
    } catch {
      return false;
    }
  }

  /**
   * Check if localStorage is available
   */
  private hasLocalStorage(): boolean {
    try {
      return typeof localStorage !== 'undefined';
    } catch {
      return false;
    }
  }

  /**
   * Check if IndexedDB is available
   */
  private hasIndexedDB(): boolean {
    try {
      return typeof indexedDB !== 'undefined';
    } catch {
      return false;
    }
  }

  /**
   * Generate SHA-256 hash using Web Crypto API
   */
  private async sha256(message: string): Promise<string> {
    // Encode the message as UTF-8
    const msgBuffer = new TextEncoder().encode(message);

    // Hash the message
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);

    // Convert ArrayBuffer to Base64
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashBase64 = btoa(String.fromCharCode.apply(null, hashArray as any));

    return hashBase64;
  }
}
